import { prefersReducedMotion } from '@theme/utilities';

/**
 * Desktop mega menu behavior for the header's nav-group <details> elements
 * (item 2 of the header rondgang). The open/close mechanics (click, Enter,
 * outside click) are native <details>/<summary> plus auto-close-details.js;
 * this only adds:
 * - hover intent (a small delay before opening and before closing, so
 *   crossing the trigger on the way to something else does not flash it
 *   open), skipped entirely on touch/coarse pointers
 * - Escape closing the menu and returning focus to its trigger
 * - aria-expanded on the trigger kept in sync with the open state
 */
const OPEN_DELAY = 150;
const CLOSE_DELAY = 250;

const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');

/** @type {HTMLDetailsElement[]} */
const allGroups = [];

/** @param {HTMLDetailsElement} group */
function initMegaMenuGroup(group) {
  allGroups.push(group);
  const summary = group.querySelector(':scope > summary');
  if (!(summary instanceof HTMLElement)) return;

  /** @type {ReturnType<typeof setTimeout> | null} */
  let openTimer = null;
  /** @type {ReturnType<typeof setTimeout> | null} */
  let closeTimer = null;
  // True while the panel is open because hover intent opened it (set in the
  // openTimer callback, cleared on close). A mouse click on the summary
  // during that session must not toggle the panel closed again, or hovering
  // in and then clicking a mega menu trigger would feel broken.
  let openedByHover = false;

  const clearTimers = () => {
    if (openTimer) clearTimeout(openTimer);
    if (closeTimer) clearTimeout(closeTimer);
    openTimer = null;
    closeTimer = null;
  };

  const syncExpanded = () => {
    summary.setAttribute('aria-expanded', group.open ? 'true' : 'false');
  };

  group.addEventListener('toggle', () => {
    syncExpanded();
    if (!group.open) {
      clearTimers();
      openedByHover = false;
      return;
    }
    // Only one mega panel open at a time: hovering from item to item before
    // its close delay fires would otherwise stack two panels.
    for (const other of allGroups) {
      if (other !== group && other.open) other.open = false;
    }
  });
  syncExpanded();

  if (!finePointer.matches) return;

  group.addEventListener('pointerenter', (event) => {
    if (event.pointerType !== 'mouse') return;
    clearTimers();
    if (group.open) return;
    openTimer = setTimeout(() => {
      group.open = true;
      openedByHover = true;
    }, OPEN_DELAY);
  });

  // Once hover intent opened the panel, a mouse click on the summary would
  // otherwise re-toggle it (native <details> behavior) and close it right
  // back, which reads as a broken hover menu. Keep it open for that click;
  // keyboard activation (Enter/Space fires click with event.detail === 0)
  // keeps its normal toggle behavior, and touch never reaches here since
  // this whole block is gated on the fine-pointer check above.
  summary.addEventListener('click', (event) => {
    if (openedByHover && group.open && event.detail !== 0) {
      event.preventDefault();
      // Only the first click after a hover-open gets suppressed; clear the
      // flag right away so a second click in the same hover session toggles
      // the panel normally instead of being swallowed too.
      openedByHover = false;
    }
  });

  // Closing on mouse-out never fires while focus is still inside the panel
  // (tabbing through its links with the mouse resting elsewhere), and
  // tabbing all the way out closes it immediately instead of waiting for a
  // mouse event that may never come (Codex review findings).
  const closeUnlessFocused = () => {
    if (group.contains(document.activeElement)) return;
    group.open = false;
  };

  group.addEventListener('pointerleave', (event) => {
    if (event.pointerType !== 'mouse') return;
    clearTimers();
    if (!group.open) return;
    closeTimer = setTimeout(closeUnlessFocused, prefersReducedMotion() ? 0 : CLOSE_DELAY);
  });

  group.addEventListener('focusout', (event) => {
    if (!group.open) return;
    const next = event.relatedTarget;
    if (next instanceof Node && group.contains(next)) return;
    group.open = false;
  });
}

/** @param {Element} root */
function initMegaMenu(root) {
  for (const group of root.querySelectorAll('.header__nav-group')) {
    if (group instanceof HTMLDetailsElement && !allGroups.includes(group)) initMegaMenuGroup(group);
  }
}

initMegaMenu(document);

// A single document-level listener, not the per-group one this replaced:
// a hover-opened panel keeps focus wherever it was before the hover (often
// outside the group entirely), so a keydown listener scoped to the group
// never sees that Escape press.
document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;
  const openGroup = allGroups.find((group) => group.open);
  if (!openGroup) return;
  const summary = openGroup.querySelector(':scope > summary');
  const focusWasInside = openGroup.contains(document.activeElement);
  event.preventDefault();
  openGroup.open = false;
  if (focusWasInside && summary instanceof HTMLElement) summary.focus();
});

// The theme editor replaces the header section's markup in place on save;
// a plain querySelectorAll pass at module load never sees that replacement
// DOM, so new groups would open with no hover intent, no Escape handling
// and a stuck aria-expanded (Codex review finding). This has no effect on
// the live storefront, where the event never fires.
document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof Element) initMegaMenu(event.target);
});

document.addEventListener('shopify:section:unload', (event) => {
  const removed = event.target instanceof Element ? [...event.target.querySelectorAll('.header__nav-group')] : [];
  for (const group of removed) {
    const index = allGroups.indexOf(group);
    if (index !== -1) allGroups.splice(index, 1);
  }
});
